import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { FormAuditLogTable } from 'src/components/organisms/FormAuditLogTable';
import { FormPreview } from 'src/components/organisms/FormPreview';
import PageNotFound from 'src/components/PageNotFound';
import { useDuplicateFormManagementQuestionnaireMutation } from 'src/graphql';
import { usePreventOutsideClickOnToast } from 'src/hooks/usePreventOutsideClickOnToast';
import { useFormCategories } from './hooks/useFormCategories';
import { FormCreationModals, Header } from './components';
import { QuestionTypesProvider } from './contexts/QuestionTypesContext';
import { FormCreationMainColumn, PageWrapper } from './FormCreation.styles';
import { useFormCreationHandler } from './formCreationHandler';
import { FormCreationQuestionArea } from './FormCreationQuestionArea';
import { buildFormDraftTranslateContentInput } from './helpers/buildFormDraftTranslateContentInput';
import { fingerprintFormTranslateSource } from './helpers/fingerprintFormTranslateSource';
import {
  runArchiveFormManagementQuestionnaire,
  runDuplicateFormManagementQuestionnaire,
} from './helpers/formCreationDuplicateAndArchiveActions';
import { DEFAULT_FORM_EDITING_LOCALE } from './helpers/localizedQuestionFieldHelpers';
import { mergeFormTranslateDraftContentIntoQuestions } from './helpers/mergeFormTranslateDraftContentIntoQuestions';
import { FormCreationMode } from './types';

export type { DuplicateFromState } from './types';

interface FormCreationProps {
  initialMode?: FormCreationMode;
}

export const FormCreation = ({
  initialMode = 'initial',
}: FormCreationProps) => {
  usePreventOutsideClickOnToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  // Fetch form categories (cached from CoiFormManagement page)
  const {
    formCategoryMap,
    error: categoriesError,
    isReady: categoriesReady,
  } = useFormCategories();

  // Show error if categories failed to load
  useEffect(() => {
    if (categoriesError) {
      handleErrorToast({
        toastMessage: 'Failed to load form type',
        toastDescription:
          'Unable to fetch form type data. Please refresh the page.',
      });
    }
  }, [categoriesError]);

  const formCreation = useFormCreationHandler({
    initialMode,
    formCategoryMap,
    categoriesReady,
  });
  const { isSpeakUpForm, questions, translateFormDraftContent, setQuestions } =
    formCreation;
  const { mutate: duplicateQuestionnaire, isLoading: isDuplicating } =
    useDuplicateFormManagementQuestionnaireMutation();

  const [previewSelectedLanguage, setPreviewSelectedLanguage] = useState<
    string | null
  >(null);
  const [isPreviewTranslating, setIsPreviewTranslating] = useState(false);
  const previewTranslationInFlightRef = useRef<Set<string>>(new Set());
  const previewTranslateRequestGenerationRef = useRef(0);
  const previewTranslateSourceFingerprintByLocaleRef = useRef<
    Record<string, string>
  >({});
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  // Get formCategoryId for question types filtering
  const formCategoryId =
    formCreation.questionnaireData?.formManagementQuestionnaireById
      ?.formCategory?.id ??
    (formCategoryMap
      ? isSpeakUpForm
        ? formCategoryMap['speak-up']
        : formCategoryMap['conflict-of-interest']
      : undefined);

  const previewLanguageOptions = useMemo(() => {
    return [...formCreation.selectedLanguages]
      .sort((a, b) => {
        const labelComparison = a.label.localeCompare(b.label);

        if (labelComparison !== 0) {
          return labelComparison;
        }

        return a.value.localeCompare(b.value);
      })
      .map((l) => ({
        value: l.value,
        label: l.label,
      }));
  }, [formCreation.selectedLanguages]);

  const previewTranslateFingerprintResetKey =
    formCreation.questionnaireIdFromParams ??
    formCreation.questionnaireId ??
    null;

  useEffect(() => {
    previewTranslateRequestGenerationRef.current += 1;
    previewTranslateSourceFingerprintByLocaleRef.current = {};
    previewTranslationInFlightRef.current.clear();
    setIsPreviewTranslating(false);
  }, [previewTranslateFingerprintResetKey]);

  const handlePreviewLanguageChange = useCallback(
    (languageCode: string) => {
      setPreviewSelectedLanguage(languageCode);
      if (!languageCode || languageCode === DEFAULT_FORM_EDITING_LOCALE) return;
      const translateSourceFingerprint = fingerprintFormTranslateSource(
        questions,
        formCreation.title,
        formCreation.description,
      );
      if (
        previewTranslateSourceFingerprintByLocaleRef.current[languageCode] ===
        translateSourceFingerprint
      ) {
        return;
      }
      if (previewTranslationInFlightRef.current.has(languageCode)) return;
      const requestGeneration = previewTranslateRequestGenerationRef.current;
      previewTranslationInFlightRef.current.add(languageCode);
      setIsPreviewTranslating(true);
      const input = buildFormDraftTranslateContentInput({
        targetLanguageCode: languageCode,
        formTitle: formCreation.title,
        formDescription: formCreation.description,
        questions,
      });
      translateFormDraftContent({ input })
        .then((data) => {
          if (
            requestGeneration !== previewTranslateRequestGenerationRef.current
          ) {
            return;
          }
          previewTranslateSourceFingerprintByLocaleRef.current[languageCode] =
            translateSourceFingerprint;
          setQuestions((prev) =>
            mergeFormTranslateDraftContentIntoQuestions(
              prev,
              languageCode,
              data.translateFormDraftContent,
            ),
          );
        })
        .catch(() => {
          if (
            requestGeneration !== previewTranslateRequestGenerationRef.current
          ) {
            return;
          }
          handleErrorToast({ toastMessage: 'Translation failed' });
        })
        .finally(() => {
          if (
            requestGeneration !== previewTranslateRequestGenerationRef.current
          ) {
            return;
          }
          previewTranslationInFlightRef.current.delete(languageCode);
          if (previewTranslationInFlightRef.current.size === 0) {
            setIsPreviewTranslating(false);
          }
        });
    },
    [
      questions,
      formCreation.title,
      formCreation.description,
      translateFormDraftContent,
      setQuestions,
    ],
  );

  const handleDuplicateClick = useCallback(() => {
    if (!formCreation.questionnaireData?.formManagementQuestionnaireById) {
      return;
    }
    setShowDuplicateModal(true);
  }, [formCreation.questionnaireData]);

  const handleDuplicateConfirm = useCallback(
    (mode: 'all-languages' | 'default-only', newTitle: string) => {
      if (
        !formCreation.questionnaireId ||
        !formCreation.questionnaireData?.formManagementQuestionnaireById
      )
        return;

      const titleToUse =
        newTitle.trim() ||
        formCreation.questionnaireData.formManagementQuestionnaireById
          .publicTitle ||
        '';

      runDuplicateFormManagementQuestionnaire({
        duplicateQuestionnaire,
        questionnaireId: formCreation.questionnaireId,
        publicTitle: titleToUse,
        mode,
        setShowDuplicateModal,
      });
    },
    [
      duplicateQuestionnaire,
      formCreation.questionnaireData,
      formCreation.questionnaireId,
    ],
  );

  const handleArchiveConfirm = useCallback(() => {
    if (
      !formCreation.questionnaireId ||
      !formCreation.questionnaireData?.formManagementQuestionnaireById
    )
      return;

    const savedTitle =
      formCreation.questionnaireData.formManagementQuestionnaireById
        .publicTitle || '';

    runArchiveFormManagementQuestionnaire({
      updateQuestionnaireStatus: formCreation.updateQuestionnaireStatus,
      queryClient,
      questionnaireId: formCreation.questionnaireId,
      publicTitle: savedTitle,
      setShowArchiveModal,
      setPreventNavigation: formCreation.setPreventNavigation,
      history,
      returnTo: formCreation.returnTo,
    });
  }, [
    formCreation.questionnaireData,
    formCreation.questionnaireId,
    formCreation.updateQuestionnaireStatus,
    formCreation.setPreventNavigation,
    formCreation.returnTo,
    history,
    queryClient,
  ]);

  if (formCreation.formType === null) {
    return <PageNotFound />;
  }

  return (
    <PageWrapper
      data-testid="form-creation-page"
      $scrollLocked={formCreation.isPreviewOpen || formCreation.isAuditLogOpen}
    >
      <Header
        mode={formCreation.mode}
        onBackClick={formCreation.handleBackClick}
        onSave={formCreation.handleSaveDraft}
        onPublish={formCreation.handlePublish}
        onPreview={formCreation.handlePreview}
        isSaving={formCreation.currentAction === 'draft'}
        isPublishing={formCreation.currentAction === 'publish'}
        isActiveForm={formCreation.isActiveForm}
        questionnaireId={formCreation.questionnaireId}
        onDeleteSuccess={formCreation.handleDeleteSuccess}
        onDuplicateClick={
          formCreation.isEditMode ? handleDuplicateClick : undefined
        }
        onArchiveClick={
          formCreation.isEditMode && formCreation.isActiveForm
            ? () => setShowArchiveModal(true)
            : undefined
        }
        onAuditLogClick={formCreation.handleAuditLogClick}
        isEditMode={formCreation.isEditMode}
        duplicateManagementSessionActive={
          formCreation.duplicateManagementSessionActive
        }
        saveDraftDisabled={formCreation.saveDraftDisabled}
        publishDisabled={formCreation.publishFormDisabled}
        previewDisabled={formCreation.previewDisabled}
      />
      <QuestionTypesProvider formCategoryId={formCategoryId}>
        <FormCreationMainColumn>
          <FormCreationQuestionArea
            mode={formCreation.mode}
            isSpeakUpForm={formCreation.isSpeakUpForm}
            selectedLanguages={formCreation.selectedLanguages}
            onAddDefaultLanguage={formCreation.handleAddDefaultLanguageWrapper}
            setSelectedLanguages={formCreation.setSelectedLanguages}
            onCategorySelect={formCreation.handleCategorySelect}
            isEditMode={formCreation.isEditMode}
            title={formCreation.title}
            setTitle={formCreation.setTitle}
            description={formCreation.description}
            setDescription={formCreation.setDescription}
            titleTranslations={formCreation.titleTranslations}
            setTitleTranslations={formCreation.setTitleTranslations}
            descriptionTranslations={formCreation.descriptionTranslations}
            setDescriptionTranslations={formCreation.setDescriptionTranslations}
            titleTouched={formCreation.titleTouched}
            setTitleTouched={formCreation.setTitleTouched}
            descriptionTouched={formCreation.descriptionTouched}
            setDescriptionTouched={formCreation.setDescriptionTouched}
            categoryName={formCreation.categoryName}
            setCategoryName={formCreation.setCategoryName}
            setCategoryId={formCreation.setCategoryId}
            categoryNameTouched={formCreation.categoryNameTouched}
            setCategoryNameTouched={formCreation.setCategoryNameTouched}
            categoryDescription={formCreation.categoryDescription}
            setCategoryDescription={formCreation.setCategoryDescription}
            categoryDescriptionTouched={formCreation.categoryDescriptionTouched}
            setCategoryDescriptionTouched={
              formCreation.setCategoryDescriptionTouched
            }
            selectedCountry={formCreation.selectedCountry}
            setSelectedCountry={formCreation.setSelectedCountry}
            countryTouched={formCreation.countryTouched}
            setCountryTouched={formCreation.setCountryTouched}
            autoReviewEnabled={formCreation.autoReviewEnabled}
            setAutoReviewEnabled={formCreation.setAutoReviewEnabled}
            onSpeakUpCategoryChosenInConfig={
              formCreation.onSpeakUpCategoryChosenInConfig
            }
            questions={formCreation.questions}
            setQuestions={formCreation.setQuestions}
            questionRefs={formCreation.questionRefs}
            isDuplicateManagementDraft={
              formCreation.duplicateManagementSessionActive
            }
            translateFingerprintSeedKey={
              formCreation.questionnaireIdFromParams ??
              formCreation.questionnaireId ??
              null
            }
            setActiveEditingLocaleRef={formCreation.activeEditingLocaleRef}
          />
        </FormCreationMainColumn>
        <FormPreview
          variant="slideout"
          isOpen={formCreation.isPreviewOpen}
          onClose={(open) => {
            formCreation.setIsPreviewOpen(open);
            if (!open) {
              setPreviewSelectedLanguage(null);
            }
          }}
          title={formCreation.title}
          description={formCreation.description}
          languageOptions={previewLanguageOptions}
          questions={formCreation.questions}
          selectedLanguage={previewSelectedLanguage ?? undefined}
          onLanguageChange={handlePreviewLanguageChange}
          isTranslating={isPreviewTranslating}
        />
        <FormAuditLogTable
          isOpen={formCreation.isAuditLogOpen}
          setIsOpen={formCreation.setIsAuditLogOpen}
          questionnaireId={formCreation.questionnaireId ?? undefined}
          questionnaireTitle={formCreation.title}
        />
      </QuestionTypesProvider>
      <FormCreationModals
        showCancellationModal={formCreation.showCancellationModal}
        hasUnsavedChanges={formCreation.hasUnsavedChanges}
        preventNavigation={formCreation.preventNavigation}
        setNextLocation={formCreation.setNextLocation}
        setShowCancellationModal={formCreation.setShowCancellationModal}
        onLeave={formCreation.onLeave}
        onStay={formCreation.onStay}
        showPublishConfirmModal={formCreation.showPublishConfirmModal}
        setShowPublishConfirmModal={formCreation.setShowPublishConfirmModal}
        handlePublishConfirm={formCreation.handlePublishConfirm}
        showDuplicateModal={showDuplicateModal}
        setShowDuplicateModal={setShowDuplicateModal}
        handleDuplicateConfirm={handleDuplicateConfirm}
        isDuplicating={isDuplicating}
        showArchiveModal={showArchiveModal}
        setShowArchiveModal={setShowArchiveModal}
        handleArchiveConfirm={handleArchiveConfirm}
        handleArchiveCancel={() => setShowArchiveModal(false)}
        currentAction={formCreation.currentAction}
        formType={formCreation.formType!}
      />
    </PageWrapper>
  );
};
